export const CGM_READER_DEVICE_ID = "goodflip-cgm-reader";

export const CGM_WARMUP_DURATION_MS = 65 * 60 * 1000; // 65 minutes
export const CGM_WARMUP_DURATION_SECONDS = 65 * 60;

export const CGM_SENSOR_CYCLE_DAYS = 14;

export const CGM_GLUCOSE_RANGE = {
  MIN: 50,
  MAX: 350,
} as const;

/** Pie chart config for cumulative "Time in Ranges" - keys match API response */
export const CGM_PIE_CHART_DATA = [
  {
    name: "Time In Range",
    key: "timeInRangePercent",
    range: "70 - 100 mg/dl",
    ideal: ">75%",
    color: "#22C55E",
    value: 0,
  },
  {
    name: "Level 1 Hyperglycaemia",
    key: "hyper1Percent",
    range: "180 - 250 mg/dl",
    ideal: "<10%",
    color: "#FDE68A",
    value: 0,
  },
  {
    name: "Level 2 Hyperglycaemia",
    key: "hyper2Percent",
    range: ">250 mg/dl",
    ideal: "<5%",
    color: "#FACC15",
    value: 0,
  },
  {
    name: "Level 1 Hypoglycaemia",
    key: "hypo1Percent",
    range: "54 - 69 mg/dl",
    ideal: "<4%",
    color: "#FCA5A5",
    value: 0,
  },
  {
    name: "Level 2 Hypoglycaemia",
    key: "hypo2Percent",
    range: "<54 mg/dl",
    ideal: "<1%",
    color: "#EF4444",
    value: 0,
  },
] as const;

export const CGM_IN_RANGE = {
  MIN: 70,
  MAX: 180,
} as const;

export const CGM_Y_AXIS_TICKS = [50, 100, 150, 200, 250, 300, 350];

export type CgmConnectionStep =
  | "intro"
  | "bluetooth"
  | "scanning"
  | "qr"
  | "device-ready"
  | "place-sensor"
  | "place-transmitter"
  | "binding"
  | "warmup"
  | "connected"
  | "error";

/** Steps where the UI is still the connection journey (not CGM status / warmup screen). */
export function isCgmConnectionJourneyStep(step: CgmConnectionStep): boolean {
  return (
    step === "intro" ||
    step === "bluetooth" ||
    step === "scanning" ||
    step === "qr" ||
    step === "binding" ||
    step === "device-ready" ||
    step === "place-sensor" ||
    step === "place-transmitter"
  );
}

export type CgmSensorState = 0 | 1 | 2;

export const SENSOR_STATE_LABELS: Record<CgmSensorState, string> = {
  0: "Ready",
  1: "Unbound",
  2: "Warming Up",
};

/**
 * Tracky SDK sensor state codes mapped to human-readable names.
 * Sent from RN via CGM_SENSOR_STATE event.
 */
export const TRACKY_SENSOR_STATES: Record<number, string> = {
  101: "CONNECT_START",
  102: "CONNECTED",
  103: "BIND_SUCCESS",
  104: "BIND_FAIL",
  105: "LOW_POWER",
  106: "TEMPERATURE_ERROR",
  107: "DISCONNECTED",
  108: "TIMEOUT",
  109: "PULL_START",
  110: "PULL_IN_PROGRESS",
  111: "PULL_END",
  112: "UNBIND_START",
  113: "UNBIND_SUCCESS",
  114: "UNBIND_RESET",
  115: "ONLINE_LOST",
  116: "SENSOR_MISMATCH",
};

/**
 * CGM status values used for the post-connection status-driven UI.
 * Matches the reference app's CGM_STATUS_CONFIG pattern.
 */
export type CgmDisplayStatus =
  | "NOT_CONNECTED"
  | "BLUETOOTH_OFF"
  | "WARMUP"
  | "CONNECTED"
  /** BLE / link loss — same binding; can auto-reconnect (BE `DISCONNECTED`). */
  | "DISCONNECTED"
  /** Full transmitter unpair — restart connection journey (BE `TRANSMITTER_DISCONNECT`). */
  | "TRANSMITTER_DISCONNECT"
  | "EXPIRED"
  | "LOW_POWER"
  | "TEMPERATURE_ERROR"
  | "TIMEOUT"
  | "SENSOR_MISMATCH"
  | "SYNCING"
  | "ERROR";

export interface CgmDisplayStatusConfig {
  label: string;
  color: string;
  bgColor: string;
  type: "info" | "warning" | "success" | "danger";
  description: string;
  showReconnect: boolean;
  showTroubleshooting: boolean;
}

export const CGM_DISPLAY_STATUS_CONFIG: Record<
  CgmDisplayStatus,
  CgmDisplayStatusConfig
> = {
  NOT_CONNECTED: {
    label: "Not Connected",
    color: "#6B7280",
    bgColor: "#F3F4F6",
    type: "info",
    description: "CGM device is not connected. Start the connection process.",
    showReconnect: false,
    showTroubleshooting: false,
  },
  BLUETOOTH_OFF: {
    label: "Bluetooth off",
    color: "#6B7280",
    bgColor: "#EEF2FF",
    type: "info",
    description:
      "Phone Bluetooth is off. Turn it on so your CGM can connect and sync data.",
    showReconnect: false,
    showTroubleshooting: false,
  },
  WARMUP: {
    label: "Warming Up",
    color: "#F59E0B",
    bgColor: "#FFFBEB",
    type: "warning",
    description:
      "Your sensor is warming up. Readings will be available after the warmup period.",
    showReconnect: false,
    showTroubleshooting: false,
  },
  CONNECTED: {
    label: "Connected",
    color: "#10B981",
    bgColor: "#ECFDF5",
    type: "success",
    description: "CGM is connected and syncing glucose data.",
    showReconnect: false,
    showTroubleshooting: true,
  },
  DISCONNECTED: {
    label: "Disconnected",
    color: "#D97706",
    bgColor: "#FFFBEB",
    type: "warning",
    description:
      "Connection to the transmitter was lost temporarily. It may reconnect automatically, or you can try Reconnect.",
    showReconnect: true,
    showTroubleshooting: true,
  },
  TRANSMITTER_DISCONNECT: {
    label: "Transmitter disconnected",
    color: "#EF4444",
    bgColor: "#FEF2F2",
    type: "danger",
    description:
      "The transmitter was fully disconnected. Start the connection process again to pair your CGM.",
    showReconnect: false,
    showTroubleshooting: true,
  },
  EXPIRED: {
    label: "Sensor Expired",
    color: "#6B7280",
    bgColor: "#F3F4F6",
    type: "info",
    description:
      "This sensor cycle has ended. Connect a new CGM to continue — start the connection process.",
    showReconnect: false,
    showTroubleshooting: true,
  },
  LOW_POWER: {
    label: "Low Power",
    color: "#F59E0B",
    bgColor: "#FFFBEB",
    type: "warning",
    description: "Transmitter battery is low. Readings may be intermittent.",
    showReconnect: false,
    showTroubleshooting: true,
  },
  TEMPERATURE_ERROR: {
    label: "Temperature Error",
    color: "#EF4444",
    bgColor: "#FEF2F2",
    type: "info",
    description:
      "Sensor temperature is out of range. Move to a comfortable environment.",
    showReconnect: false,
    showTroubleshooting: true,
  },
  TIMEOUT: {
    label: "Connection Timeout",
    color: "#EF4444",
    bgColor: "#FEF2F2",
    type: "danger",
    description:
      "Connection timed out. Make sure the device is nearby and try again.",
    showReconnect: true,
    showTroubleshooting: true,
  },
  SENSOR_MISMATCH: {
    label: "Sensor Mismatch",
    color: "#EF4444",
    bgColor: "#FEF2F2",
    type: "info",
    description:
      "Sensor and transmitter mismatch detected. Please check your device.",
    showReconnect: false,
    showTroubleshooting: true,
  },
  SYNCING: {
    label: "Syncing",
    color: "#3B82F6",
    bgColor: "#EFF6FF",
    type: "info",
    description: "Syncing missing glucose data with the server...",
    showReconnect: false,
    showTroubleshooting: false,
  },
  ERROR: {
    label: "Error",
    color: "#EF4444",
    bgColor: "#FEF2F2",
    type: "danger",
    description: "An error occurred with the CGM device.",
    showReconnect: true,
    showTroubleshooting: true,
  },
};

/**
 * Map a Tracky sensor state number to a CgmDisplayStatus.
 */
export function sensorStateToDisplayStatus(
  sensorState: number
): CgmDisplayStatus | null {
  switch (sensorState) {
    case 102:
      return "CONNECTED";
    case 105:
      return "LOW_POWER";
    case 106:
      return "TEMPERATURE_ERROR";
    case 107:
    case 108:
    case 115:
      return "DISCONNECTED";
    case 116:
      return "SENSOR_MISMATCH";
    default:
      return null;
  }
}

export const cgmDetailedLogsScreens = {
  DAY_GLUCOSE_GRAPH: "day-glucose-graph",
  DAILY_GLUCOSE_FLUCTUATION: "daily-glucose-fluctuation",
  HIGH_OR_LOW_GLUCOSE_AREAS: "high-or-low-glucose-areas",
  GLUCOSE_CONCENTRATION_PROFILE: "glucose-concentration-profile",
  TIME_BELOW_RANGE: "time-below-range",
  TIME_ABOVE_RANGE: "time-above-range",
  TIME_IN_RANGE: "time-in-range",
  AVG_GLUCOSE: "avg-glucose",
  MAX_GLUCOSE: "max-glucose",
  MIN_GLUCOSE: "min-glucose",
  AGP_OVERLAP_GRAPH: "agp-overlap-graph",
  FLUCTUATION_SUMMARY: "fluctuation-summary",
  HBA1C: "hba1c",
  GLUCOSE_VARIABILITY: "glucose-variability",
  STANDARD_DEVIATION: "standard-deviation",
  PERCENTAGE_TIME_IN_RANGES: "percentage-time-in-ranges",
};
